import { useContext, useLayoutEffect, useRef, useState } from "react";
import { BurgerButtonBig, ButtonRound, DropdownInfo, DropdownMenu, HeaderBig, LearnMore, LinkSmart, LoadButton, SignupBox, SquareButton, TextInput, TextInputMultiline } from "../common/Common";
import './Contact.css';
import { LocaleContext, LoginContext, OverlayContext, StateContext } from "../common/App";
import { LoadState, Location, Salutation } from '../common/enums.mjs';
import { checkEmail, get_translation_objects, postWithCsrf, sanitizeStr } from "../common/util";
import { Footer } from "./WebsiteCommon";
import { maxContactQueryMessageLength, maxNameLength, maxPhoneNoLength, maxUsernameLength } from "../common/globals_common.mjs";
import { validateName, validatePhoneNumber } from "../common/validate";
import { Link } from "react-router-dom";
import { useIndexUpdate, useReCaptcha } from "../common/hooks";

export function Contact(props) {
    const state = useContext(StateContext);
    const login = useContext(LoginContext);
    const overlay = useContext(OverlayContext);
    const locale = useContext(LocaleContext);

    const [sendResultText, setSendResultText] = useState(null);
    const [contactQuerySent, setContactQuerySent] = useState(false);

    const [sendState, setSendState] = useState(LoadState.NONE);

    const [location, setLocation] = useState(Location.NONE);
    const [salutation, setSalutation] = useState(Salutation.NONE);
    const [name, setName] = useState("");
    const [telephoneNo, setTelephoneNo] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [messageError, setMessageError] = useState(false);

    const [conditionsAccepted, setConditionsAccepted] = useState(false);

    const [locationTransList, locationTransListInverse] = get_translation_objects("location_long", Location, locale);
    const [salutationTransList, salutationTransListInverse] = get_translation_objects("salutation", Salutation, locale);

    useIndexUpdate(props.indexUpdate, locale);

    const fetchReCaptchaToken = useReCaptcha();


    // FUNCTIONS:

    function checkInput() {
        if(!conditionsAccepted ||
                location === Location.NONE ||
                !salutation || salutation === Salutation.NONE ||
                !name || name === "" || name.length > (maxNameLength) ||
                telephoneNo === null || (telephoneNo && telephoneNo.length > maxPhoneNoLength) ||  // Empty is allowed
                !email || email === "" || email.length > maxUsernameLength ||
                !message || message === "" || message.length > maxContactQueryMessageLength || messageError)
        {
            return false;
        }

        return true;
    }

    function handleSend() {
        try {
            let displayError = true;

            fetchReCaptchaToken().then((token) => {
                let dic = {
                    location: location,
                    salutation: salutation,
                    name: name,
                    telephone: telephoneNo,
                    email: email,
                    message: message,
                    recaptcha_token: token
                };

                return postWithCsrf("/user/contact_query", JSON.stringify(dic), state).then((response) => {
                    if(response.ok) {
                        setSendResultText(<div className="text_success" dangerouslySetInnerHTML={{__html: locale.msgs.CONTACT_QUERY_SUCCESS.replace("${{email}}", sanitizeStr(email))}}></div>);
                        setContactQuerySent(true);

                        setLocation(Location.NONE);
                        setSalutation("");
                        setTelephoneNo("");
                        setEmail("");
                        setMessage("");
                        setSendState(LoadState.LOAD_SUCCESS);
                        return;
                    }

                    if(response.status == 527) {
                        return response.text().then(() => {
                            overlay.setErrorPopupInfo({"title": locale.msgs.BOT_PROTECTION_ERROR, "text": locale.msgs.BOT_PROTECTION_ERROR_DESCR});
                            overlay.setErrorPopupDisplayed(true);

                            displayError = false;
                            throw new Error("Response code: " + response.status);
                        });
                    }
                    else {
                        throw new Error("Response code: " + response.status);
                    }
                });
            }).catch((error) => {
                if(displayError) {
                    setSendState(LoadState.LOAD_ERROR);
                    setSendResultText(<div className="text_error">{locale.msgs.CONTACT_QUERY_FAILED}</div>);
                }
                else {
                    setSendState(LoadState.NONE);
                }
            });

            // Calling setSendState() after postWithCsrf() as it could throw an exception
            setSendState(LoadState.LOADING);
        }
        catch(e) {
            console.log(e);
        }
    }

    function handleTelNoClick() {
        window.location.href = "tel:" + locale.msgs.TELEPHONE_NO_NO;
    }


    // RENDER:

    return (
        <div className="contact head_img">
            <img className="img_width" src="/customer/images/contact_1.jpg" alt={locale.msgs.CONTACT_IMG_1_ALT_TEXT}></img>

            <div className="text_box">
                <h1 className="text_big_big head_after_img">{locale.msgs.CONTACT}</h1>
                <div className="text" dangerouslySetInnerHTML={{__html: locale.msgs.CONTACT_DESCR}}></div>

                { login.loggedIn ?
                    <div className="contact_tel_no_box" onClick={handleTelNoClick}>
                        <img src="/common/icon_phone-colored.svg" />
                        <div className="contact_tel_no">{locale.msgs.TELEPHONE_NO_NO}</div>
                    </div>
                :
                    null
                }
            </div>

            <div className="text_box margin_bottom_high contact_data_box">
                <div className="contact_box">
                    <div className="text_big_2">{locale.msgs.CONTACT_FORM}</div>
                    <div className="text">{locale.msgs.QUESTIONS_OR_SUGGESTIONS}</div>

                    { contactQuerySent ? null :
                        <div className="contact_input_element_box">
                            <div className="contact_input_element_first">
                                <DropdownMenu text={locale.msgs.MN_LOCATION} options={Object.values(locationTransList)}
                                        value={locationTransList[location]}
                                        onValueChange={(value) => setLocation(locationTransListInverse[value])}
                                        onScroll={props.onScroll}
                                        textSearch={true} />
                            </div>
                            
                            <div className="contact_input_element">
                                <DropdownMenu text={locale.msgs.SALUTATION} options={Object.values(salutationTransList)}
                                        value={salutationTransList[salutation]}
                                        onValueChange={(value) => setSalutation(salutationTransListInverse[value])}
                                        onScroll={props.onScroll} />
                            </div>

                            <div className="contact_input_element">
                                <TextInput text={locale.msgs.NAME} value={name}
                                        onChange={(value) => setName(value)}
                                        autocomplete="name"
                                        name="name"
                                        errorText={locale.msgs.PLEASE_ENTER_VALID_NAME}
                                        checker={(value) => validateName(value)}
                                        maxLength={maxNameLength}
                                />
                            </div>

                            <div className="contact_input_element">
                                <TextInput type="tel" text={locale.msgs.TELEPHONE_NO + " (" + locale.msgs.OPTIONAL + ")"} value={telephoneNo}
                                        onChange={(value) => setTelephoneNo(value)}
                                        optional={true}
                                        autocomplete="tel"
                                        name="tel"
                                        errorText={locale.msgs.ENTER_VALID_PHONE_NO}
                                        checker={(value) => validatePhoneNumber(value)}
                                        maxLength={maxPhoneNoLength} />
                            </div>

                            <div className="contact_input_element">
                                <TextInput type="email" text={locale.msgs.EMAIL} value={email}
                                        onChange={(value) => setEmail(value)}
                                        checker={(v) => checkEmail(v)}
                                        autocomplete="email"
                                        name="email"
                                        errorText={locale.msgs.ENTER_VALID_EMAIL_ADDRESS}
                                        maxLength={maxUsernameLength}
                                />
                            </div>

                            <div className="contact_input_element">
                                <TextInputMultiline text={locale.msgs.MESSAGE} value={message}
                                        onChange={(value) => setMessage(value)}
                                        showPlaceholderIfNotEmpty={true} height="14rem"
                                        maxLength={maxContactQueryMessageLength}
                                        onError={setMessageError}/>
                            </div>

                            <div className="checkbox_box" id="contact_conditions_box">
                                <input type="checkbox" name="conditions" id="conditions_checkbox" className="input_radio_check checkbox"
                                        onChange={(e) => setConditionsAccepted(e.target.checked)} checked={conditionsAccepted}></input>
                                <label className="noselect checkbox_label" htmlFor="conditions_checkbox">

                                    {locale.msgs.ACCEPT_TOS_1}
                                    <LinkSmart className="link_2" to={"/terms_of_use/"} target="_blank">{locale.msgs.TOS_2}</LinkSmart>
                                    {locale.msgs.ACCEPT_TOS_2}
                                    <LinkSmart className="link_2" to={"/privacy_policy_contact/"} target="_blank">{locale.msgs.PRIVACY_POLICY_CONTACT}</LinkSmart>
                                    {locale.msgs.ACCEPT_TOS_3}

                                </label>
                            </div>

                            <div className="contact_send_button_box">
                                <LoadButton text={locale.msgs.SEND} clickable={checkInput()} onClick={handleSend}
                                        saving={sendState === LoadState.LOADING} />
                            </div>
                        </div>
                    }

                    <div className="send_result_box">
                        {sendResultText}
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
}
